import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, ExternalLink, CheckCircle, Shield } from 'lucide-react';
import axios from 'axios';
import { SEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BEST_FOR = {
  fashionphile: 'Guaranteed authenticity',
  therealreal: 'Wide selection & frequent deals',
  '1stdibs': 'Rare & vintage pieces',
  vestiaire: 'European luxury & community trade',
};

const StarRating = ({ rating }) => (
  <div className="flex items-center gap-1.5">
    <span className="flex">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${
            i < Math.floor(rating) ? 'text-[#C69C6D] fill-[#C69C6D]' : 'text-[#E5E0D8]'
          }`}
          strokeWidth={1}
        />
      ))}
    </span>
    <span className="text-sm font-heading text-[#111]">{rating}/5</span>
  </div>
);

export const PartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await axios.get(`${API}/partners`);
        setPartners(response.data);
      } catch (error) {
        console.error('Error fetching partners:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="partners-page" className="bg-[#FAF9F6]">
      <SEO
        title="Trusted Pre-Owned Jewelry Partners"
        description="Vetted resale platforms for pre-owned luxury jewelry: Fashionphile, The RealReal, 1stDibs and more — reviewed by GIA-certified specialists."
        url="/partners"
      />

      {/* Editorial Hero */}
      <section className="border-b border-[#E5E0D8]">
        <div className="container-editorial py-20 lg:py-28">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-12">
            <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-[#111]">Trusted Partners</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <p className="overline overline-gold mb-6 animate-fade-in">Vetted Marketplaces</p>
              <h1 className="heading-editorial heading-editorial-lg text-[#111] animate-fade-in-up">
                Where We <br />
                <span className="italic">Recommend</span> You Shop
              </h1>
            </div>
            <div className="lg:col-span-4 lg:border-l border-[#E5E0D8] lg:pl-8">
              <p className="text-[#555] leading-relaxed mb-6">
                We've personally tested every platform we recommend. Each partner offers expert
                authentication, condition reports, and buyer protection.
              </p>
              <div className="space-y-2">
                <p className="text-xs tracking-[0.15em] uppercase text-[#555] flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-[#C69C6D]" strokeWidth={1.5} /> Expert Authentication
                </p>
                <p className="text-xs tracking-[0.15em] uppercase text-[#555] flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-[#C69C6D]" strokeWidth={1.5} /> Buyer Protection
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners — Editorial List */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">The Roster</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl">Our Recommended Platforms</h2>
            </div>
            <p className="hidden md:block overline">{partners.length} Vetted</p>
          </div>

          <div className="space-y-px bg-[#E5E0D8]">
            {partners.map((partner, index) => (
              <article
                key={partner.id}
                className="bg-[#FAF9F6] grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 lg:p-12 group"
                data-testid={`partner-section-${partner.slug}`}
              >
                {/* Number + Name */}
                <div className="lg:col-span-3">
                  <p className="font-heading text-4xl lg:text-5xl text-[#C69C6D] font-light mb-4">
                    {String(index + 1).padStart(2, '0')}
                  </p>
                  <h2 className="font-heading text-3xl text-[#111] mb-3 group-hover:text-[#C69C6D] transition-colors">
                    {partner.name}
                  </h2>
                  <StarRating rating={partner.rating} />
                </div>

                {/* Description + Pros */}
                <div className="lg:col-span-6 lg:border-l lg:border-r border-[#E5E0D8] lg:px-8">
                  <p className="overline overline-gold mb-3">{partner.specialty || 'Marketplace'}</p>
                  <p className="text-[#555] leading-relaxed mb-6">{partner.description}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="overline mb-3">Highlights</p>
                      <ul className="space-y-2">
                        {(partner.pros || []).slice(0, 3).map((pro, i) => (
                          <li key={i} className="text-sm text-[#555] flex items-start gap-2">
                            <span className="w-1 h-1 bg-[#C69C6D] rounded-full mt-2 flex-shrink-0" />
                            {pro}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="overline mb-3">Best For</p>
                      <p className="font-heading text-lg text-[#111] leading-snug">
                        {BEST_FOR[partner.slug] || 'Pre-owned luxury jewelry'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTAs */}
                <div className="lg:col-span-3 flex flex-col gap-3 lg:justify-center">
                  <a
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-editorial-inverse btn-editorial inline-flex items-center justify-center gap-2"
                    data-testid={`shop-${partner.slug}-btn`}
                  >
                    Shop {partner.name}
                    <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                  </a>
                  <Link
                    to={`/partners/${partner.slug}`}
                    className="btn-editorial inline-flex items-center justify-center gap-2"
                  >
                    Full Review
                    <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliate Note */}
      <section className="border-t border-[#E5E0D8] py-12 bg-white/40">
        <div className="container-editorial">
          <div className="max-w-3xl mx-auto text-center">
            <p className="overline overline-gold mb-4">Editorial Transparency</p>
            <p className="text-[#555] leading-relaxed mb-4">
              We earn a commission when you make a purchase through our affiliate links. This
              helps support our content and keeps our guides free for readers. Recommendations
              are based on thorough research and genuine expertise — never commission rate.
            </p>
            <Link
              to="/affiliate-disclosure"
              className="text-xs tracking-[0.15em] uppercase border-b border-[#111] pb-1 inline-flex items-center gap-2 hover:text-[#C69C6D] hover:border-[#C69C6D] transition-colors"
            >
              Read Full Disclosure
              <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* Compare CTA */}
      <section className="bg-[#111] text-white py-20 lg:py-28">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="overline text-[#C69C6D] mb-6">Need Help Deciding?</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl text-white">
                Compare All <span className="italic">Platforms</span>
              </h2>
            </div>
            <div className="lg:text-right">
              <p className="text-white/60 leading-relaxed mb-8 max-w-md lg:ml-auto">
                Side-by-side comparison of pricing, authentication processes, return policies,
                and editor scores across every platform we cover.
              </p>
              <Link to="/compare" className="btn-editorial border-white text-white hover:bg-white hover:text-[#111] inline-flex items-center gap-2">
                View Comparison
                <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
